'use client';

/**
 * RangeSlider — Morfi's dual-thumb min–max range primitive.
 *
 * Sibling of {@link AnimatedElasticSlider} (single-thumb). Reuses the same
 * design tokens, track geometry, thumb styling and keyboard model, extended to
 * two independent thumbs with a fill segment between them. The value label
 * (`Any · ≤ · ≥ · range`) and end labels match the filter-overhaul mockup at
 * /tmp/morfi-filters exactly — see {@link formatRangeValueLabel} for the
 * labelling contract, which is shared with the SortFilterBar active-chip bar so
 * a slider and its summary chip never disagree.
 *
 * Values are expressed in the dimension's native units (dollars, counts, %),
 * NOT percentages — the component maps to/from track percent internally.
 */
import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import type { ChangeEvent as ReactChangeEvent, FocusEvent as ReactFocusEvent, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useRef, useState } from 'react';
import { EPSILON, formatRangeEndpoint, formatRangeValueLabel, MILLION, type RangeFormat, type RangeFormatSpec, THOUSAND } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';

// ───────────────────────────────────────────────────────────────────────────
// Endpoint field formatting + parsing
// ───────────────────────────────────────────────────────────────────────────

/** Currency / percent affix shown beside an editable endpoint field. */
function fieldAffix(format: RangeFormat, symbol: string): {
  prefix: string;
  suffix: string;
} {
  if (format === 'money') return {
    prefix: symbol,
    suffix: ''
  };
  if (format === 'pct') return {
    prefix: '',
    suffix: '%'
  };
  return {
    prefix: '',
    suffix: ''
  };
}

/** The editable value of an endpoint field — bare digits with a k/M suffix for
 *  money (the `$` is rendered as an affix, not typed). */
function endpointFieldValue(value: number, format: RangeFormat): string {
  const rounded = Math.round(value);
  if (format !== 'money') return String(rounded);
  const abs = Math.abs(rounded);
  if (abs >= MILLION) return `${String(rounded / MILLION).replace(/\.0$/, '')}M`;
  if (abs >= THOUSAND) return `${String(rounded / THOUSAND).replace(/\.0$/, '')}k`;
  return String(rounded);
}

/** Parse a typed endpoint (`"5k"`, `"1.5M"`, `"5,000"`, `"$5000"`, `"50%"`). */
function parseEndpoint(text: string): number | null {
  let t = text.trim().toLowerCase().replace(/[,$%\s]/g, '');
  let mult = 1;
  if (t.endsWith('k')) {
    mult = THOUSAND;
    t = t.slice(0, -1);
  } else if (t.endsWith('m')) {
    mult = MILLION;
    t = t.slice(0, -1);
  }
  const n = Number.parseFloat(t);
  return Number.isNaN(n) ? null : n * mult;
}

// ───────────────────────────────────────────────────────────────────────────
// Math helpers
// ───────────────────────────────────────────────────────────────────────────

function clampValue(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function roundToStep(value: number, step: number, min: number): number {
  const snapped = Math.round((value - min) / step) * step + min;
  // Avoid floating dust for non-integer steps (e.g. step 0.5).
  const decimals = (() => {
    const s = step.toString();
    const dot = s.indexOf('.');
    return dot === -1 ? 0 : s.length - dot - 1;
  })();
  if (decimals === 0) return Math.round(snapped);
  const factor = 10 ** decimals;
  return Math.round(snapped * factor) / factor;
}
function toPercent(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return (value - min) / (max - min) * 100;
}

// ───────────────────────────────────────────────────────────────────────────
// Public surface
// ───────────────────────────────────────────────────────────────────────────

export interface RangeSliderProps {
  /** Visible label rendered above the track. */
  label: string;
  min: number;
  max: number;
  /** Step granularity. Defaults to a sensible value derived from the span. */
  step?: number | undefined;
  /** Controlled `[low, high]` in native units. */
  value: [number, number];
  /** Called with the next `[low, high]` on drag / keyboard step. */
  onChange: (next: [number, number]) => void;
  /** Format + label spec. */
  format: RangeFormatSpec;
  /** Optional layout-only className passthrough. */
  className?: string;
}
function defaultStep(min: number, max: number): number {
  const span = max - min;
  if (span <= 0) return 1;
  if (span <= 24) return 1;
  if (span >= 100_000) return 5000;
  if (span >= 10_000) return 1000;
  if (span >= 1000) return 100;
  if (span >= 100) return 5;
  return 1;
}
interface EndpointFieldProps {
  ariaLabel: string;
  prefix: string;
  suffix: string;
  value: string;
  onChange: (event: ReactChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onCommit: (text: string) => void;
}

/** A compact, keyboard-friendly editable endpoint for one end of the range.
 *  `inputMode="decimal"` gives mobile a numeric pad; desktop can still type
 *  shorthand like `5k` / `1.5M` (parsed in {@link parseEndpoint}). */
function EndpointField(t0) {
  const $ = _c(18);
  const {
    ariaLabel,
    prefix,
    suffix,
    value,
    onChange,
    onFocus,
    onCommit
  } = t0;
  let t1;
  if ($[0] !== prefix) {
    t1 = prefix.length > 0 ? <span aria-hidden="true" className="select-none text-foreground-tertiary">{prefix}</span> : null;
    $[0] = prefix;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onFocus) {
    t2 = event => {
      onFocus();
      event.currentTarget.select();
    };
    $[2] = onFocus;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onCommit) {
    t3 = event_0 => onCommit(event_0.target.value);
    $[4] = onCommit;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== ariaLabel || $[7] !== onChange || $[8] !== t2 || $[9] !== t3 || $[10] !== value) {
    t4 = <input type="text" inputMode="decimal" enterKeyHint="done" autoComplete="off" aria-label={ariaLabel} value={value} onChange={onChange} onFocus={t2} onBlur={t3} onKeyDown={_temp} className="field-sizing-content min-w-[1ch] max-w-[7ch] bg-transparent text-left font-medium text-foreground-primary outline-none" />;
    $[6] = ariaLabel;
    $[7] = onChange;
    $[8] = t2;
    $[9] = t3;
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== suffix) {
    t5 = suffix.length > 0 ? <span aria-hidden="true" className="select-none text-foreground-tertiary">{suffix}</span> : null;
    $[12] = suffix;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t4 || $[16] !== t5) {
    t6 = <label className="inline-flex items-center gap-0.5 rounded-control border border-border-primary bg-background-primary px-2.5 py-1.5 text-caption tabular-nums transition-colors hover:border-border-brand focus-within:border-border-brand">{t1}{t4}{t5}</label>;
    $[14] = t1;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function _temp(event_1) {
  if (event_1.key === "Enter") {
    event_1.currentTarget.blur();
  }
}
export function RangeSlider(t0) {
  const $ = _c(84);
  const {
    label,
    min,
    max,
    step,
    value,
    onChange,
    format,
    className
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const trackRef = useRef(null);
  const resolvedStep = step ?? defaultStep(min, max);
  const [lo, hi] = value;
  const loPct = toPercent(lo, min, max);
  const hiPct = toPercent(hi, min, max);
  let t1;
  if ($[0] !== format || $[1] !== hi || $[2] !== lo || $[3] !== max || $[4] !== min) {
    t1 = formatRangeValueLabel(lo, hi, min, max, format);
    $[0] = format;
    $[1] = hi;
    $[2] = lo;
    $[3] = max;
    $[4] = min;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const valueLabel = t1;
  const isActive = lo > min + EPSILON || hi < max - EPSILON;
  const t2 = format.symbol ?? "$";
  let t3;
  if ($[6] !== format.format || $[7] !== t2) {
    t3 = fieldAffix(format.format, t2);
    $[6] = format.format;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const affix = t3;
  const [loEdit, setLoEdit] = useState(null);
  const [hiEdit, setHiEdit] = useState(null);
  const commitLo = function commitLo(text) {
    setLoEdit(null);
    const parsed = parseEndpoint(text);
    if (parsed === null) {
      return;
    }
    const next = clampValue(roundToStep(parsed, resolvedStep, min), min, hi - resolvedStep);
    if (next !== lo) {
      onChange([next, hi]);
    }
  };
  const commitHi = function commitHi(text_0) {
    setHiEdit(null);
    const parsed_0 = parseEndpoint(text_0);
    if (parsed_0 === null) {
      return;
    }
    const next_0 = clampValue(roundToStep(parsed_0, resolvedStep, min), lo + resolvedStep, max);
    if (next_0 !== hi) {
      onChange([lo, next_0]);
    }
  };
  const valueFromClientX = function valueFromClientX(clientX) {
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width === 0) {
      return min;
    }
    const ratio = clampValue((clientX - rect.left) / rect.width, 0, 1);
    return clampValue(min + ratio * (max - min), min, max);
  };
  const handleDrag = which => event => {
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    const move = e => {
      const raw = roundToStep(valueFromClientX(e.clientX), resolvedStep, min);
      if (which === "lo") {
        const nextLo = Math.min(raw, hi - resolvedStep);
        onChange([clampValue(nextLo, min, hi - resolvedStep), hi]);
      } else {
        const nextHi = Math.max(raw, lo + resolvedStep);
        onChange([lo, clampValue(nextHi, lo + resolvedStep, max)]);
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const handleKeyDown = which_0 => event_0 => {
    const arrowStep = event_0.shiftKey ? resolvedStep * 10 : resolvedStep;
    let delta;
    bb56: switch (event_0.key) {
      case "ArrowRight":
      case "ArrowUp":
        {
          delta = arrowStep;
          break bb56;
        }
      case "ArrowLeft":
      case "ArrowDown":
        {
          delta = -arrowStep;
          break bb56;
        }
      case "Home":
        {
          delta = which_0 === "lo" ? min - lo : min - hi;
          break bb56;
        }
      case "End":
        {
          delta = which_0 === "lo" ? max - lo : max - hi;
          break bb56;
        }
      default:
        {
          return;
        }
    }
    event_0.preventDefault();
    if (which_0 === "lo") {
      const nextLo_0 = clampValue(roundToStep(lo + delta, resolvedStep, min), min, hi - resolvedStep);
      onChange([nextLo_0, hi]);
    } else {
      const nextHi_0 = clampValue(roundToStep(hi + delta, resolvedStep, min), lo + resolvedStep, max);
      onChange([lo, nextHi_0]);
    }
  };
  let t4;
  if ($[9] !== className) {
    t4 = cn("[--range-slider-track:var(--background-tertiary)]", "[--range-slider-fill:var(--button-bg)]", "[--range-slider-thumb-bg:var(--background-primary)]", "[--range-slider-thumb-border:var(--button-bg)]", "select-none px-0.5 pt-0.5", className);
    $[9] = className;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = isActive ? "text-foreground-primary" : "text-foreground-secondary";
  let t6;
  if ($[11] !== t5) {
    t6 = cn("shrink-0 text-caption font-medium tabular-nums", t5);
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6 || $[14] !== valueLabel) {
    t7 = <span className={t6}>{valueLabel}</span>;
    $[13] = t6;
    $[14] = valueLabel;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = `${label} minimum value`;
  let t9;
  if ($[16] !== format.format || $[17] !== lo || $[18] !== loEdit) {
    t9 = loEdit ?? endpointFieldValue(lo, format.format);
    $[16] = format.format;
    $[17] = lo;
    $[18] = loEdit;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_0 => setLoEdit(e_0.target.value);
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== format.format || $[22] !== lo) {
    t11 = () => setLoEdit(endpointFieldValue(lo, format.format));
    $[21] = format.format;
    $[22] = lo;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== affix.prefix || $[25] !== affix.suffix || $[26] !== commitLo || $[27] !== t11 || $[28] !== t8 || $[29] !== t9) {
    t12 = <EndpointField ariaLabel={t8} prefix={affix.prefix} suffix={affix.suffix} value={t9} onChange={t10} onFocus={t11} onCommit={commitLo} />;
    $[24] = affix.prefix;
    $[25] = affix.suffix;
    $[26] = commitLo;
    $[27] = t11;
    $[28] = t8;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span aria-hidden="true" className="text-caption text-foreground-tertiary">–</span>;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = `${label} maximum value`;
  let t15;
  if ($[32] !== format.format || $[33] !== hi || $[34] !== hiEdit) {
    t15 = hiEdit ?? endpointFieldValue(hi, format.format);
    $[32] = format.format;
    $[33] = hi;
    $[34] = hiEdit;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e_1 => setHiEdit(e_1.target.value);
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== format.format || $[38] !== hi) {
    t17 = () => setHiEdit(endpointFieldValue(hi, format.format));
    $[37] = format.format;
    $[38] = hi;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== affix.prefix || $[41] !== affix.suffix || $[42] !== commitHi || $[43] !== t14 || $[44] !== t15 || $[45] !== t17) {
    t18 = <EndpointField ariaLabel={t14} prefix={affix.prefix} suffix={affix.suffix} value={t15} onChange={t16} onFocus={t17} onCommit={commitHi} />;
    $[40] = affix.prefix;
    $[41] = affix.suffix;
    $[42] = commitHi;
    $[43] = t14;
    $[44] = t15;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t12 || $[48] !== t18) {
    t19 = <div className="flex items-center gap-1.5">{t12}{t13}{t18}</div>;
    $[47] = t12;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t19 || $[51] !== t7) {
    t20 = <div className="mb-1 flex items-center justify-between gap-2">{t7}{t19}</div>;
    $[50] = t19;
    $[51] = t7;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const t21 = `${loPct}%`;
  const t22 = `${Math.max(0, hiPct - loPct)}%`;
  let t23;
  if ($[53] !== t21 || $[54] !== t22) {
    t23 = <div data-slot="range-slider-fill" aria-hidden="true" className="absolute inset-y-0 rounded-full bg-(--range-slider-fill)" style={{
      left: t21,
      width: t22
    }} />;
    $[53] = t21;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== format || $[57] !== lo) {
    t24 = formatRangeEndpoint(lo, format);
    $[56] = format;
    $[57] = lo;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  const t25 = handleDrag("lo");
  let t26;
  if ($[59] !== handleKeyDown) {
    t26 = handleKeyDown("lo");
    $[59] = handleKeyDown;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  const t27 = shouldReduceMotion === true ? undefined : "transition-transform active:scale-110";
  let t28;
  if ($[61] !== t27) {
    t28 = cn("absolute top-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full", "border-2 border-(--range-slider-thumb-border) bg-(--range-slider-thumb-bg) shadow-elevation-card outline-none", "focus-visible:ring-2 focus-visible:ring-border-primary/50", t27);
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  const t29 = `${loPct}%`;
  let t30;
  if ($[63] !== t29) {
    t30 = {
      left: t29
    };
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  const t31 = "slider";
  const t32 = 0;
  const t33 = "range-slider-thumb-hi";
  const t34 = `${label} maximum`;
  const t35 = "horizontal";
  let t36;
  if ($[65] !== format || $[66] !== hi) {
    t36 = formatRangeEndpoint(hi, format);
    $[65] = format;
    $[66] = hi;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  const t37 = handleDrag("hi");
  let t38;
  if ($[68] !== handleKeyDown) {
    t38 = handleKeyDown("hi");
    $[68] = handleKeyDown;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  const t39 = shouldReduceMotion === true ? undefined : "transition-transform active:scale-110";
  let t40;
  if ($[70] !== t39) {
    t40 = cn("absolute top-1/2 size-[18px] -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full", "border-2 border-(--range-slider-thumb-border) bg-(--range-slider-thumb-bg) shadow-elevation-card outline-none", "focus-visible:ring-2 focus-visible:ring-border-primary/50", t39);
    $[70] = t39;
    $[71] = t40;
  } else {
    t40 = $[71];
  }
  const t41 = `${hiPct}%`;
  let t42;
  if ($[72] !== t41) {
    t42 = {
      left: t41
    };
    $[72] = t41;
    $[73] = t42;
  } else {
    t42 = $[73];
  }
  let t43;
  if ($[74] !== hi || $[75] !== max || $[76] !== min || $[77] !== t34 || $[78] !== t36 || $[79] !== t37 || $[80] !== t38 || $[81] !== t40 || $[82] !== t42) {
    t43 = <div role={t31} tabIndex={t32} data-slot={t33} aria-label={t34} aria-orientation={t35} aria-valuemin={min} aria-valuemax={max} aria-valuenow={hi} aria-valuetext={t36} onPointerDown={t37} onKeyDown={t38} className={t40} style={t42} />;
    $[74] = hi;
    $[75] = max;
    $[76] = min;
    $[77] = t34;
    $[78] = t36;
    $[79] = t37;
    $[80] = t38;
    $[81] = t40;
    $[82] = t42;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  return <div data-slot="range-slider" data-active={isActive} className={t4}>{t20}<div ref={trackRef} data-slot="range-slider-track" className="relative mx-3 mt-4 mb-2 h-1.5 touch-none rounded-full bg-(--range-slider-track)">{t23}<div role="slider" tabIndex={0} data-slot="range-slider-thumb-lo" aria-label={`${label} minimum`} aria-orientation="horizontal" aria-valuemin={min} aria-valuemax={max} aria-valuenow={lo} aria-valuetext={t24} onPointerDown={t25} onKeyDown={t26} className={t28} style={t30} />{t43}</div></div>;
}